import * as React from 'react';
import { useTheme } from '@veneer/theme';
import propTypes from './prop_types';
import type { IconInterface } from './types';
import ICON_PLACEHOLDER_ROUND_PATH from '../../assets/icon_placeholder_round_path';
import ICON_PLACEHOLDER_SHARP_PATH from '../../assets/icon_placeholder_sharp_path';
import useDirection from '../direction/use_direction';
import useStyles from './use_styles';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import { deprecatedColors } from '../utils/deprecated_primitives';
import { ColorType } from '../utils/color_prop';

const makeIcon = (
  name: string,
  flippable: boolean,
  {
    roundFilled = ICON_PLACEHOLDER_ROUND_PATH,
    roundLined = ICON_PLACEHOLDER_ROUND_PATH,
    sharpFilled = ICON_PLACEHOLDER_SHARP_PATH,
    sharpLined = ICON_PLACEHOLDER_SHARP_PATH,
    roundFilledRtl = roundFilled,
    roundLinedRtl = roundLined,
    sharpFilledRtl = sharpFilled,
    sharpLinedRtl = sharpLined,
  }: {
    roundFilled?: React.ReactNode;
    roundLined?: React.ReactNode;
    sharpFilled?: React.ReactNode;
    sharpLined?: React.ReactNode;
    roundFilledRtl?: React.ReactNode;
    roundLinedRtl?: React.ReactNode;
    sharpFilledRtl?: React.ReactNode;
    sharpLinedRtl?: React.ReactNode;
  },
  warnings?: {
    deprecated?: string;
    removed?: boolean;
  }
): React.ForwardRefExoticComponent<
  IconInterface &
    Omit<React.SVGAttributes<SVGSVGElement>, keyof IconInterface> &
    React.RefAttributes<SVGSVGElement>
> => {
  let warned = false;
  let colorWarned = false;

  const Icon = React.forwardRef<
    SVGSVGElement,
    IconInterface & Omit<React.SVGProps<SVGSVGElement>, keyof IconInterface>
  >(
    (
      {
        'aria-label': ariaLabel,
        'aria-hidden': ariaHidden,
        'aria-labelledby': ariaLabelledBy,
        'aria-disabled': ariaDisabled,
        customStyle,
        disabled: disabledProp = false,
        display = 'inline-block',
        filled,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        focusable = false,
        size = 24,
        label,
        viewBox = '0 0 24 24',
        color,
        ...other
      },
      ref
    ) => {
      const theme = useTheme();
      const { isRTL } = useDirection();

      if (
        process.env.NODE_ENV === 'development' &&
        warnings?.removed &&
        !warned
      ) {
        console.warn(
          `@veneer/core: The ${name} component has been deprecated and it'll be removed in the next major release.`
        );
        warned = true;
      }
      if (
        process.env.NODE_ENV === 'development' &&
        warnings?.removed &&
        !warned
      ) {
        console.warn(
          `@veneer/core: The ${name} component has been deprecated and it'll be removed in the next major release.`
        );
        warned = true;
      }

      if (
        process.env.NODE_ENV === 'development' &&
        warnings?.deprecated &&
        !warned
      ) {
        console.warn(
          [
            `@veneer/core: The ${name} component has been deprecated and it'll be removed in the next major release.`,
            `Use ${warnings.deprecated} instead.`,
          ].join(' ')
        );
        warned = true;
      }

      if (
        process.env.NODE_ENV === 'development' &&
        !colorWarned &&
        deprecatedColors.includes(color as ColorType)
      ) {
        console.warn(
          `@veneer/core: The color: ${color} has been deprecated since v3.121.0 and it'll be removed by October 30, 2025`
        );
        colorWarned = true;
      }

      const style = useStyles({
        color,
        size,
        display,
      });

      const pathData =
        theme.shape === 'sharp'
          ? filled
            ? isRTL
              ? sharpFilledRtl
              : sharpFilled
            : isRTL
              ? sharpLinedRtl
              : sharpLined
          : filled
            ? isRTL
              ? roundFilledRtl
              : roundFilled
            : isRTL
              ? roundLinedRtl
              : roundLined;

      const disabled =
        disabledProp || ariaDisabled === true || ariaDisabled === 'true';
      const isInformative = ariaLabel || ariaLabelledBy || label;
      const hide = !isInformative || disabled || ariaHidden;

      return (
        <svg
          aria-disabled={ariaDisabled}
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel || label}
          aria-hidden={hide ? true : undefined}
          role={isInformative ? 'img' : undefined}
          css={[
            style.base,
            disabled && style.disabled,
            flippable && isRTL && style.rtlFlippable,
            highContrastMediaQuery(theme),
            customStyle,
          ]}
          fill="currentColor"
          ref={ref}
          viewBox={viewBox}
          xmlns="http://www.w3.org/2000/svg"
          {...other}
        >
          {pathData}
        </svg>
      );
    }
  );

  Icon.propTypes = propTypes;

  // @ts-expect-error todo: a neater way of doing this
  Icon.isVeneerIcon = true;

  Icon.displayName = name;

  return Icon;
};

export default makeIcon;
